import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { fetchJson } from '../lib/api';

const TEXTS = {
  es: {
    title: 'Crear nueva contraseña',
    subtitle: 'Ingresa una nueva contraseña para tu cuenta',
    passwordLabel: 'Nueva contraseña',
    passwordPlaceholder: '••••••••',
    confirmLabel: 'Confirmar contraseña',
    confirmPlaceholder: '••••••••',
    resetButton: 'Actualizar contraseña',
    backToLogin: 'Volver al inicio de sesión',
    resetting: 'Actualizando...',
    success: 'Contraseña actualizada',
    successMessage: 'Tu contraseña ha sido actualizada correctamente. Ahora puedes iniciar sesión.',
    goToLogin: 'Ir a iniciar sesión',
    error: 'Error',
    invalidToken: 'Link inválido o expirado.',
    passwordMismatch: 'Las contraseñas no coinciden.',
    passwordTooShort: 'La contraseña debe tener al menos 8 caracteres.',
    resetError: 'Error al actualizar la contraseña. Intenta de nuevo.',
    passwordStrength: 'Fortaleza:',
    passwordWeak: 'Débil',
    passwordFair: 'Regular',
    passwordStrong: 'Fuerte',
    passwordVeryStrong: 'Muy fuerte',
  },
  pt: {
    title: 'Criar nova senha',
    subtitle: 'Digite uma nova senha para sua conta',
    passwordLabel: 'Nova senha',
    passwordPlaceholder: '••••••••',
    confirmLabel: 'Confirmar senha',
    confirmPlaceholder: '••••••••',
    resetButton: 'Atualizar senha',
    backToLogin: 'Voltar para login',
    resetting: 'Atualizando...',
    success: 'Senha atualizada',
    successMessage: 'Sua senha foi atualizada com sucesso. Agora você pode fazer login.',
    goToLogin: 'Ir para login',
    error: 'Erro',
    invalidToken: 'Link inválido ou expirado.',
    passwordMismatch: 'As senhas não coincidem.',
    passwordTooShort: 'A senha deve ter pelo menos 8 caracteres.',
    resetError: 'Erro ao atualizar a senha. Tente novamente.',
    passwordStrength: 'Força:',
    passwordWeak: 'Fraca',
    passwordFair: 'Regular',
    passwordStrong: 'Forte',
    passwordVeryStrong: 'Muito forte',
  },
  en: {
    title: 'Create new password',
    subtitle: 'Enter a new password for your account',
    passwordLabel: 'New password',
    passwordPlaceholder: '••••••••',
    confirmLabel: 'Confirm password',
    confirmPlaceholder: '••••••••',
    resetButton: 'Update password',
    backToLogin: 'Back to login',
    resetting: 'Updating...',
    success: 'Password updated',
    successMessage: 'Your password has been updated successfully. You can now login.',
    goToLogin: 'Go to login',
    error: 'Error',
    invalidToken: 'Link invalid or expired.',
    passwordMismatch: 'Passwords do not match.',
    passwordTooShort: 'Password must be at least 8 characters.',
    resetError: 'Error updating password. Try again.',
    passwordStrength: 'Strength:',
    passwordWeak: 'Weak',
    passwordFair: 'Fair',
    passwordStrong: 'Strong',
    passwordVeryStrong: 'Very strong',
  },
};

function getPasswordStrength(password) {
  if (!password) return 0;
  let strength = 0;
  if (password.length >= 8) strength++;
  if (password.length >= 12) strength++;
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength++;
  if (/\d/.test(password)) strength++;
  if (/[!@#$%^&*]/.test(password)) strength++;
  return strength;
}

function getStrengthLabel(strength, t) {
  if (strength <= 1) return t.passwordWeak;
  if (strength <= 2) return t.passwordFair;
  if (strength <= 3) return t.passwordStrong;
  return t.passwordVeryStrong;
}

export default function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const lang = (navigator.language || 'es').split('-')[0];
  const t = TEXTS[lang] || TEXTS.es;

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!token) {
      setError(t.invalidToken);
    }
  }, [token, t.invalidToken]);

  const passwordStrength = getPasswordStrength(password);
  const strengthLabel = getStrengthLabel(passwordStrength, t);
  const strengthPercent = (passwordStrength / 5) * 100;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (password.length < 8) {
      setError(t.passwordTooShort);
      return;
    }

    if (password !== confirm) {
      setError(t.passwordMismatch);
      return;
    }

    setLoading(true);
    try {
      const response = await fetchJson('/auth/reset-password', {
        method: 'POST',
        body: JSON.stringify({ token, newPassword: password }),
      });

      if (response.ok) {
        setSubmitted(true);
      } else {
        setError(response.error || t.resetError);
      }
    } catch {
      setError(t.resetError);
    } finally {
      setLoading(false);
    }
  };

  if (error && !submitted) {
    return (
      <div className="page-shell">
        <section className="hero-panel hero-panel--home">
          <div className="hero-copy">
            <p className="eyebrow">Error</p>
            <h1>{t.error}</h1>
            <p className="hero-text">{error}</p>
          </div>
        </section>

        <section className="home-grid">
          <article className="panel auth-panel">
            <button onClick={() => navigate('/login')} className="btn-primary">
              {t.backToLogin}
            </button>
          </article>
        </section>
      </div>
    );
  }

  return (
    <div className="page-shell">
      <section className="hero-panel hero-panel--home">
        <div className="hero-copy">
          <p className="eyebrow">Recuperación</p>
          <h1>{t.title}</h1>
          <p className="hero-text">{t.subtitle}</p>
        </div>
      </section>

      <section className="home-grid">
        <article className="panel auth-panel">
          {submitted ? (
            <div className="reset-password-success">
              <div className="success-icon">✓</div>
              <h2>{t.success}</h2>
              <p>{t.successMessage}</p>
              <button onClick={() => navigate('/login')} className="btn-primary">
                {t.goToLogin}
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="auth-form">
              <label>
                {t.passwordLabel}
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t.passwordPlaceholder}
                  required
                  minLength="8"
                />
              </label>

              {password && (
                <div className="password-strength">
                  <div className="strength-label">
                    {t.passwordStrength} <strong>{strengthLabel}</strong>
                  </div>
                  <div className="strength-bar">
                    <div
                      className={`strength-fill strength-${passwordStrength}`}
                      style={{ width: `${strengthPercent}%` }}
                    />
                  </div>
                </div>
              )}

              <label>
                {t.confirmLabel}
                <input
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder={t.confirmPlaceholder}
                  required
                  minLength="8"
                />
              </label>

              {error && <div className="alert error">{error}</div>}

              <div className="form-actions">
                <button type="submit" className="btn-primary" disabled={loading || password.length < 8}>
                  {loading ? t.resetting : t.resetButton}
                </button>
                <button
                  type="button"
                  onClick={() => navigate('/login')}
                  className="btn-ghost"
                >
                  {t.backToLogin}
                </button>
              </div>
            </form>
          )}
        </article>

        <aside className="panel quick-panel">
          <p className="eyebrow">Seguridad</p>
          <h2>Crea una contraseña fuerte</h2>
          <ul className="feature-list">
            <li>Usa mayúsculas, minúsculas y números.</li>
            <li>Evita información personal o patrones obvios.</li>
            <li>Mínimo 8 caracteres recomendado.</li>
          </ul>
        </aside>
      </section>
    </div>
  );
}
